<template>
  <base-wrapper :loading="loading">
    <div class="space-y-4">
      <div class="flex justify-end gap-2">
        <base-input
          type="text"
          :shadow="false"
          placeholder="Cari Kode Barang"
          debounce
          v-model="filter.search_product_code"
          @native-input="loadStockBatches"
        />
        <base-input
          type="text"
          :shadow="false"
          placeholder="Cari Kode Batch"
          debounce
          v-model="filter.search_code"
          @native-input="loadStockBatches"
        />
        <search-warehouse-input
          :inset="false"
          placeholder="PUSAT"
          warehouse-type="pusat,kantor_pelayanan"
          v-model="filter.warehouse"
          @change="loadStockBatches"
        />
      </div>
      <datatable
        :columns="tableColumns"
        :total="stockBatches.meta.page.total"
        :perPage="stockBatches.meta.page.perPage"
        :currentPage="stockBatches.meta.page.currentPage"
        @pagechanged="onChangePage"
      >
        <template #tbody="{ classes }">
          <tr
            v-for="stockBatch in stockBatches.data"
            :key="stockBatch.id"
            :class="[classes.tr, 'bg-white']"
            :title="stockBatch.attributes.updatedAt | formatDate"
          >
            <td :class="classes.td">
              <p class="font-bold text-gray-900">
                {{ stockBatch.attributes.code }}

                <span v-if="stockBatch.attributes.product_type === 'free'"
                  >(PROMO)</span
                >
              </p>
            </td>
            <td :class="classes.td">
              <base-select
                v-if="stockBatches.meta.can_update_pickup_method"
                :options="pickupMethodOptions"
                size="sm"
                :shadow="false"
                :disabled="
                  stockBatch.attributes.pickup_method === 'unique_qrcode'
                "
                v-model="stockBatch.attributes.pickup_method"
                @change="onChangePickupMethod(stockBatch)"
              />
              <p v-else>{{ stockBatch.attributes.pickup_method_label }}</p>
            </td>
            <td :class="classes.td">
              {{ stockBatch.attributes.warehouse_code }}
            </td>
            <td :class="classes.td">
              {{ stockBatch.attributes.product_code }}
            </td>
            <td :class="classes.td">
              {{ stockBatch.attributes.product_name }}
            </td>
            <td :class="classes.td">
              {{ stockBatch.attributes.initial_stock_qty }} /
              {{
                stockBatch.attributes.stock_qty -
                stockBatch.attributes.stock_used_qty
              }}
            </td>
            <td :class="classes.td">
              {{ stockBatch.attributes.expire_at | formatDate }}
            </td>
            <td :class="classes.td">
              {{ stockBatch.attributes.description }}
            </td>
            <td :class="[classes.td, 'text-center']">
              <toggle-button
                :labels="{ checked: 'Aktif', unchecked: 'Nonaktif' }"
                :width="stockBatch.attributes.is_active ? 60 : 75"
                v-model="stockBatch.attributes.is_active"
                @change="onChangeActive(stockBatch)"
              />
            </td>
            <td :class="[classes.td, 'text-center']">
              <template v-if="stockBatch.attributes.pickup_method !== 'unique_qrcode'">
                <base-button
                  v-if="stockBatch.attributes.product_type !== 'free'"
                  size="sm"
                  @click="onAddBonus(stockBatch)"
                >
                  <Icon icon="heroicons:plus" />
                  Stok Bonus
                </base-button>
                <base-button
                  v-else
                  size="sm"
                  @click="onAddNormal(stockBatch)"
                >
                  <Icon icon="heroicons:plus" />
                  Stok Normal
                </base-button>
              </template>
            </td>
          </tr>
        </template>
      </datatable>
    </div>

    <base-confirm
      :visible="addBonusConfirm.visible"
      icon-type="badge"
      type="success"
      title="Tambah Stok Bonus"
      confirm-text="Tambah"
      :disabled="
        addBonusConfirm.form.amount < 1 ||
        addBonusConfirm.form.amount > addBonusConfirm.stockBatchStock
      "
      @close="addBonusConfirm.visible = false"
      @confirmed="onConfirmAddBonus"
    >
      <template #message>
        <p class="text-gray-500">
          Tambah stok bonus untuk batch {{ addBonusConfirm.stockBatchCode }}
        </p>
      </template>
      <template #content>
        <base-input
          inset
          type="text"
          label="Jumlah Batch Bonus"
          placeholder="10"
          v-model="addBonusConfirm.form.amount"
        />
      </template>
    </base-confirm>

    <base-confirm
      :visible="addNormalConfirm.visible"
      icon-type="badge"
      type="success"
      title="Tambah Stok Normal"
      confirm-text="Tambah"
      :disabled="
        addNormalConfirm.form.amount < 1 ||
        addNormalConfirm.form.amount > addNormalConfirm.stockBatchStock
      "
      @close="addNormalConfirm.visible = false"
      @confirmed="onConfirmAddNormal"
    >
      <template #message>
        <p class="text-gray-500">
          Tambah stok bonus untuk batch {{ addNormalConfirm.stockBatchCode }}
        </p>
      </template>
      <template #content>
        <base-input
          inset
          type="text"
          label="Jumlah Batch Bonus"
          placeholder="10"
          v-model="addNormalConfirm.form.amount"
        />
      </template>
    </base-confirm>
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import { mapGetters, mapActions } from 'vuex';
import { ToggleButton } from 'vue-js-toggle-button';
import BaseConfirm from '../../../../../components/base/BaseConfirm.vue';
import SearchWarehouseInput from '../../../../../components/warehouse/search-warehouse-input.vue';

export default {
  mixins: [requestMixin],
  components: { ToggleButton, BaseConfirm, SearchWarehouseInput },
  data() {
    return {
      loadingStockBatch: false,
      loadingAddBonus: false,
      loadingAddNormal: false,
      filter: {
        search_product_code: null,
        search_code: null,
        warehouse: {
          id: null,
          code: null,
          name: null,
          searchCode: null,
          originalCode: null,
          area: {
            id: null,
            code: null
          }
        },
      },
      stockBatches: {
        data: [],
        meta: {
          can_update_pickup_method: false,
          page: {},
        },
      },
      addBonusConfirm: {
        visible: false,
        stockBatchId: null,
        stockBatchCode: null,
        stockBatchStock: null,
        form: {
          amount: null,
        },
      },
      addNormalConfirm: {
        visible: false,
        stockBatchId: null,
        stockBatchCode: null,
        stockBatchStock: null,
        form: {
          amount: null,
          batchId: null
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
    }),
    loading() {
      return this.loadingStockBatch || this.loadingAddBonus || this.loadingAddNormal;
    },
    pickupMethodOptions() {
      return [
        { key: 'manual', label: 'Manual', value: 'manual' },
        { key: 'qrcode', label: 'Scan QR Produk', value: 'qrcode' },
        {
          key: 'unique_qrcode',
          label: 'Scan QR Unik',
          value: 'unique_qrcode',
          disabled: true,
        },
      ];
    },
    tableColumns() {
      return [
        { id: 'code', name: 'No. Batch' },
        { id: 'type', name: 'Tipe' },
        { id: 'warehouse_code', name: 'Kode Gudang' },
        { id: 'product_code', name: 'Kode Barang' },
        { id: 'product_name', name: 'Nama Barang' },
        { id: 'product_qty', name: 'Stok Awal / Stok Tersedia' },
        { id: 'expire_at', name: 'Kadaluarsa' },
        { id: 'description', name: 'Keterangan' },
        { id: 'status', name: 'Status', theadClass: 'text-center' },
        { id: 'action', name: 'Aksi', theadClass: 'text-center' },
      ];
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    async loadStockBatches(params) {
      this.loadingStockBatch = true;

      const [res, err] = await this.request(
        `/api/v1/stock-batches`,
        {
          params: {
            'fields[stock-batches]':
              'code,product_type,product_code,product_name,warehouse_code,initial_stock_qty,stock_qty,is_active,stock_used_qty,expire_at,pickup_method,description,pickup_method_label,product,updatedAt',
            'fields[products]': 'code',
            'include': 'product',
            'page[size]': 5,
            'filter[search_product_code]': this.filter.search_product_code,
            'filter[search_code]': this.filter.search_code,
            ...(this.filter.warehouse.id
              ? { 'filter[warehouse_id]': this.filter.warehouse.id }
              : { 'filter[warehouse_type_in]': 'pusat,kantor_pelayanan' }
            ),
            'sort': '-updatedAt',
            ...params,
          },
        }
      );

      if (!err) {
        this.stockBatches = res;
      }

      this.loadingStockBatch = false;
    },
    onChangePage(page) {
      this.loadStockBatches({
        'page[number]': page,
      });
    },
    async onChangeActive(stockBatch) {
      this.loadingStockBatch = true;

      await this.request(`/api/v1/stock-batches/${stockBatch.id}`, {
        method: 'patch',
        data: {
          data: {
            type: 'stock-batches',
            id: stockBatch.id,
            attributes: {
              is_active: stockBatch.attributes.is_active,
            },
          },
        },
      });

      this.loadStockBatches();

      this.loadingStockBatch = false;
    },
    async onChangePickupMethod(stockBatch) {
      this.loadingStockBatch = true;

      await this.request(`/api/v1/stock-batches/${stockBatch.id}`, {
        method: 'patch',
        data: {
          data: {
            type: 'stock-batches',
            id: stockBatch.id,
            attributes: {
              pickup_method: stockBatch.attributes.pickup_method,
            },
          },
        },
      });

      this.loadStockBatches();

      this.loadingStockBatch = false;
    },
    onAddBonus(stockBatch) {
      this.addBonusConfirm.stockBatchId = stockBatch.id;
      this.addBonusConfirm.stockBatchCode = stockBatch.attributes.code;
      this.addBonusConfirm.stockBatchStock =
        stockBatch.attributes.stock_qty - stockBatch.attributes.stock_used_qty;
      this.addBonusConfirm.form.amount = null;
      this.addBonusConfirm.visible = true;
    },
    async onAddNormal(stockBatch) {
      this.addNormalConfirm.stockBatchId = stockBatch.id;
      this.addNormalConfirm.stockBatchCode = stockBatch.attributes.code;
      this.addNormalConfirm.stockBatchStock =
        stockBatch.attributes.stock_qty - stockBatch.attributes.stock_used_qty;
      
      this.addNormalConfirm.form.batchId = null;
      this.addNormalConfirm.form.amount = null;

      this.addNormalConfirm.visible = true;
    },
    async onConfirmAddBonus() {
      this.loadingAddBonus = true;

      const [, err] = await this.request(
        `/api/v1/stock-batches/${this.addBonusConfirm.stockBatchId}/-actions/add-bonus`,
        {
          method: 'post',
          data: this.addBonusConfirm.form,
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });
      } else {
        this.createAlert({
          status: 'success',
          data: 'Berhasil menambahkan stok bonus',
        });

        this.addBonusConfirm.visible = false;

        this.loadStockBatches();
      }

      this.loadingAddBonus = false;
    },
    async onConfirmAddNormal() {
      this.loadingAddNormal = true;

      const [, err] = await this.request(
        `/api/v1/stock-batches/${this.addNormalConfirm.stockBatchId}/-actions/add-normal`,
        {
          method: 'post',
          data: {
            batch_id: this.addNormalConfirm.form.batchId,
            amount: this.addNormalConfirm.form.amount
          },
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });
      } else {
        this.createAlert({
          status: 'success',
          data: 'Berhasil menambahkan stok bonus',
        });

        this.addNormalConfirm.visible = false;

        this.loadStockBatches();
      }

      this.loadingAddNormal = false;
    }
  },
  created() {
    this.loadStockBatches();
  },
};
</script>
